import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  FileAction,
  FormStatus,
  Page,
  PatientSmartFormSubmission,
  SmartFormAnalysisResult,
  SmartFormAnalysisReport,
  SmartFormAnalysisStatusResponse,
  SmartFormSubmission,
  SmartFormSubmissionResponse,
  SubmissionResults,
} from '@models';
import { SmartFormSubmissionService, Utils } from '@services';
import { HttpService } from '../http.service';

@Injectable({
  providedIn: 'root',
})
export class SmartFormSubmissionApiService
  extends HttpService<SmartFormSubmission>
  implements SmartFormSubmissionService
{
  protected override readonly collectionPath = '/smart-forms/submissions';

  /**
   * GET /smart-forms/submissions
   */
  getSubmissions(params?: {
    status?: FormStatus;
    page: number;
    pageSize: number;
    patientName?: string;
    orderBy?: string;
    desk?: boolean;
  }): Observable<Page<SmartFormSubmission>> {
    return this.getCollectionByQuery(params);
  }

  /**
   * GET /smart-forms/patients/{patientId}/submissions
   */
  getPatientSubmissions(
    patientId: string
  ): Observable<PatientSmartFormSubmission[]> {
    return this.http.get<PatientSmartFormSubmission[]>(
      `${this.apiRootUrl}/smart-forms/patients/${patientId}/submissions`
    );
  }

  /**
   * GET /smart-forms/submissions/{submissionId}
   */
  getSubmissionById(
    submissionId: number | string
  ): Observable<SmartFormSubmissionResponse> {
    return this.getItemById<SmartFormSubmissionResponse>(submissionId);
  }

  /**
   * PATCH /smart-forms/submissions/{submissionId}
   */
  patchSubmission(
    submissionId: number | string,
    payload: Partial<SmartFormSubmission>
  ): Observable<SmartFormSubmission> {
    return this.updateItem(submissionId, payload);
  }

  /**
   * DELETE /smart-forms/submissions/{submissionId}
   */
  deleteSubmission(submissionId: string): Observable<void> {
    return this.deleteItemById(submissionId) as Observable<void>;
  }

  /**
   * GET /smart-forms/submissions/{submissionId}/results
   */
  getSubmissionResults(
    submissionId: number | string
  ): Observable<SubmissionResults> {
    return this.http.get<SubmissionResults>(
      `${this.apiUrl}/${submissionId}/results`
    );
  }

  /**
   * GET /smart-forms/submissions/{submissionId}/smart-form-analysis
   */
  getSubmissionSmartFormAnalysis(
    submissionId: number | string
  ): Observable<SmartFormAnalysisReport> {
    return this.http.get<SmartFormAnalysisReport>(
      `${this.apiUrl}/${submissionId}/smart-form-analysis`
    );
  }

  /**
   * GET /smart-forms/submissions/{submissionId}/smart-form-analysis/status
   */
  getSubmissionSmartFormAnalysisStatus(
    submissionId: number | string
  ): Observable<SmartFormAnalysisStatusResponse | SmartFormAnalysisReport> {
    return this.http.get<
      SmartFormAnalysisStatusResponse | SmartFormAnalysisReport
    >(`${this.apiUrl}/${submissionId}/smart-form-analysis/status`);
  }

  /**
   * POST /smart-forms/submissions/{submissionId}/smart-form-analysis/patient-handout/share
   */
  shareSubmissionSmartFormAnalysisPatientHandout(
    submissionId: string,
    fullPatientHandout: boolean
  ): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.apiUrl}/${submissionId}/smart-form-analysis/patient-handout/share?fullPatientHandout=${fullPatientHandout}`,
      {}
    );
  }

  /**
   * PUT /smart-forms/submissions/{submissionId}/smart-form-analysis/chartnote-summary
   */
  updateSubmissionSmartFormAnalysisChartnoteSummary(
    submissionId: string,
    chartnotes: string
  ): Observable<SmartFormAnalysisResult> {
    return this.http.put<SmartFormAnalysisResult>(
      `${this.apiUrl}/${submissionId}/smart-form-analysis/chartnote-summary`,
      { chartNoteSummary: chartnotes }
    );
  }

  /**
   * POST /smart-forms/submissions/{submissionId}/submit
   */
  submitSubmission(submissionId: number | string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${submissionId}/submit`, {});
  }

  /**
   * GET /smart-forms/submissions/{submissionId}/patient-name
   */
  getPatientNameBySubmissionId(id: string): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/${id}/patient-name`);
  }

  /**
   * POST /smart-forms/submissions/{submissionId}/reminder
   */
  sendReminder(id: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/reminder`, {});
  }

  /**
   * GET /smart-forms/submissions/{submissionId}/pdf
   */
  getSubmissionPdf(id: string, action: FileAction): Observable<Blob> {
    return this.getSubmissionPdfWithMeta(id, action).pipe(
      map(result => result.blob)
    );
  }

  /**
   * GET /smart-forms/submissions/{submissionId}/analysis-pdf
   */
  getAnalysisPdf(id: string, action: FileAction): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/analysis-pdf`, {
      responseType: 'blob',
      params: this.getHttpParameters({ action }),
    }) as Observable<Blob>;
  }

  getSubmissionPdfWithMeta(
    id: string,
    action: FileAction
  ): Observable<{
    blob: Blob;
    fileName: string | null;
    disposition: string | null;
  }> {
    return (
      this.http.get(`${this.apiUrl}/${id}/pdf`, {
        responseType: 'blob',
        observe: 'response',
        params: this.getHttpParameters({ action }),
      }) as Observable<HttpResponse<Blob>>
    ).pipe(
      map(response => {
        const disposition = response.headers.get('Content-Disposition');
        const fileName = Utils.getFileNameFromDisposition(disposition);

        return {
          blob: (response.body ?? new Blob()) as Blob,
          fileName,
          disposition,
        };
      })
    );
  }
}
